import React from 'react';
import { SvgIcon } from '@progress/kendo-react-common';
import { circleIcon } from '@progress/kendo-svg-icons';

import {
  FacilitatorChatContext,
  FacilitatorChatContextProps
} from '../../contexts/facilitator-chat';
import { ChatState } from '../../types/state/websocket/chat-state';
import { PlayerResponse } from '../../types/responses/player-response';
import { NotificationIndicator } from '../notification-indicator';

type ListType = 'global-chat' | 'player-chat' | 'team-chat' | 'group-chat';

interface ListProps {
  selectedItem:
    | {
        id: string;
        name: string;
        type: ListType;
      }
    | undefined;
  setSelectedItem: React.Dispatch<
    React.SetStateAction<
      | {
          id: string;
          name: string;
          type: ListType;
        }
      | undefined
    >
  >;
  listData: PlayerResponse[];
  listType: ListType;
}

export const FacilitatorList = ({
  listData,
  listType,
  selectedItem,
  setSelectedItem
}: ListProps) => {
  const { newChat, setNewChat } = React.useContext(
    FacilitatorChatContext
  ) as FacilitatorChatContextProps;

  const newChatCounter = React.useCallback(
    (item: PlayerResponse) => {
      let result = 0;

      if (listType === 'global-chat') {
        result = newChat.filter((c) => c.group === 'global-chat').length;
      } else if (listType === 'team-chat') {
        const teamChat = newChat.filter(
          (c) => c.group === 'team-chat' && c.author.teamId === item.code
        );
        result = teamChat.length;
      } else if (listType === 'player-chat') {
        const playerChat = newChat.filter(
          (c) => c.group === 'player-chat' && c.author.id === item.code
        );
        result = playerChat.length;
      }

      return result;
    },
    [listType, newChat.length]
  );

  const onClick = React.useCallback(
    (item: PlayerResponse) => {
      setSelectedItem({
        id: item.code ?? '',
        name: item.name ?? '',
        type: listType
      });
      if (listType === 'global-chat') {
        setNewChat((prev) => prev.filter((c) => c.group !== 'global-chat'));
      } else {
        const type: Record<typeof listType, keyof ChatState['author']> = {
          'group-chat': 'groupId',
          'team-chat': 'teamId',
          'player-chat': 'id'
        };

        setNewChat((prev) =>
          prev.filter(
            (c) =>
              !(c.group === listType && c.author[type[listType]] === item.code)
          )
        );
      }
    },
    [listType]
  );

  return (
    <div className="list-group w-100">
      {listData.map((item) => {
        const counter = newChatCounter(item);
        return (
          <div
            key={item.code}
            className={`list-group-item cursor-pointer ${
              item.code === selectedItem?.id ? 'selected-list' : ''
            }`}
            onClick={() => onClick(item)}>
            <div
              className={'d-flex align-items-center justify-content-between'}>
              <small
                className={`${
                  item.code === selectedItem?.id || counter > 0
                    ? 'text-dark fw-bold'
                    : 'text-dark'
                }`}>
                {item?.name}
              </small>
              {counter > 0 && (
                <NotificationIndicator counter={counter} bgColor={'#4698CB'} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
